// Data Sources page for Google Vertex AI: one tab per connected Google Cloud org.
import { useCallback, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    changeVertexScope,
    createVertexIntegration,
    deleteVertexIntegration,
    replaceVertexKey,
} from "../../lib/vertexClient.js";
import {
    NO_DIRECTORY,
    VERTEX_SCOPED_DIRECTORY,
    directoryMissing,
    directoryScoped,
    vertexBlocked,
    vertexSteps,
    vertexTurningOn,
    whenLabel,
} from "./model.js";
import {
    AsideSection,
    ErrorText,
    Field,
    Panel,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    Tabs,
    inputCls,
    linkCls,
} from "../../components/setupSteps.jsx";
import { TONE, dangerBtnCls, primaryBtnCls, secondaryBtnCls } from "../../components/statusCard.jsx";

const NEW_TAB = "new";

const AUDIT_LOG_FILTER =
    'protoPayload.serviceName="aiplatform.googleapis.com" AND logName:"cloudaudit.googleapis.com%2Fdata_access"';

const ROLE_PERMISSIONS = [
    "aiplatform.endpoints.setPublisherModelConfig",
    "aiplatform.endpoints.fetchPublisherModelConfig",
    "aiplatform.operations.list",
    "bigquery.tables.get",
    "bigquery.tables.update",
];

const LOOKUP_PERMISSION = "resourcemanager.projects.get";

function rolePermissions(lookup) {
    return lookup ? [...ROLE_PERMISSIONS, LOOKUP_PERMISSION] : ROLE_PERMISSIONS;
}

function projectList(text) {
    return [...new Set(text.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean))];
}

function orgTone(integration) {
    if (integration.status === "deleting") return TONE.WAITING;
    const [, logging, capture] = vertexSteps(integration);
    if (logging === "failed" || capture === "failed") return TONE.ATTENTION;
    if (logging !== "done" || capture !== "done") return TONE.WAITING;
    return TONE.OK;
}

function Mono({ children }) {
    return <span className="font-mono text-[11.5px]">{children}</span>;
}

function Ui({ children }) {
    return <span className="font-medium text-[color:var(--ink)]">{children}</span>;
}

function Copy({ label, value }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    }, [value]);
    return (
        <span className="mt-[4px] mb-[8px] flex min-w-0 items-stretch gap-[6px] last:mb-0">
            <code
                data-copy={label}
                title={value}
                className="min-w-0 flex-1 truncate border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[5px] font-mono text-[11.5px] text-[color:var(--ink)]"
            >
                {value}
            </code>
            <button type="button" onClick={copy} aria-label={`Copy ${label}`} className={secondaryBtnCls}>
                {copied ? "Copied" : "Copy"}
            </button>
        </span>
    );
}

function Checklist({ children }) {
    return <div className="grid gap-[16px] text-[12.5px] leading-[1.5] text-[color:var(--muted)]">{children}</div>;
}

function Group({ title, children }) {
    return (
        <section className="grid gap-[6px]">
            <h4 className="m-0 text-[12px] font-medium text-[color:var(--ink)]">
                {title}
            </h4>
            <ul className="m-0 grid list-none gap-[6px] p-0">{children}</ul>
        </section>
    );
}

function Task({ sub = false, check = true, children }) {
    return (
        <li className={`grid grid-cols-[16px_minmax(0,1fr)] items-start gap-x-[8px] ${sub ? "pl-[24px]" : ""}`}>
            {check ? <input type="checkbox" aria-label="Done" className="mt-[3px] self-start" /> : <span />}
            <div className="min-w-0">{children}</div>
        </li>
    );
}

function Where({ children }) {
    return <span className="font-medium text-[color:var(--ink)]">{children}</span>;
}

function RouteLogs({ org, project, dataset }) {
    return (
        <Checklist>
            <Group title={`With ${project} selected in the project picker`}>
                <Task>
                    <Where>BigQuery</Where>: create a dataset named <Mono>{dataset}</Mono>.
                </Task>
                <Task>
                    <Where>IAM & Admin &gt; Audit Logs</Where>: select <Ui>Agent Platform API</Ui> (older consoles:
                    Vertex AI API), tick <Ui>Data Read</Ui>, and Save.
                </Task>
            </Group>
            <Group title={`With the org ${org} selected in the project picker`}>
                <Task>
                    <Where>Logging &gt; Log Router &gt; Create sink</Where>: name it anything, for example{" "}
                    <Mono>origin-vertex-audit</Mono>.
                </Task>
                <Task sub>
                    Sink service: <Ui>Other resource</Ui>, with this destination:
                    <Copy
                        label="Sink destination"
                        value={`bigquery.googleapis.com/projects/${project}/datasets/${dataset}`}
                    />
                </Task>
                <Task sub>
                    <Ui>Use partitioned tables</Ui>: on.
                </Task>
                <Task sub>
                    Choose logs to include in sink:{" "}
                    <Ui>Include logs ingested by this organization and all child resources</Ui>. It is not the default.
                </Task>
                <Task sub>
                    Inclusion filter:
                    <Copy label="Inclusion filter" value={AUDIT_LOG_FILTER} />
                </Task>
            </Group>
            <Group title={`With ${project} selected in the project picker`}>
                <Task>
                    <Where>BigQuery &gt; {dataset} &gt; Share</Where>: give the sink's writer identity{" "}
                    <Ui>BigQuery Data Editor</Ui>.
                    <Copy label="Sink writer identity" value={`service-org-${org}@gcp-sa-logging.iam.gserviceaccount.com`} />
                </Task>
            </Group>
            <Group title="For every other project that calls Vertex AI">
                <Task>
                    With that project selected, <Where>IAM & Admin &gt; Audit Logs</Where>: select{" "}
                    <Ui>Agent Platform API</Ui>, tick <Ui>Data Read</Ui>, and Save.
                </Task>
                <Task>
                    With <Mono>{project}</Mono> selected, <Where>BigQuery &gt; {dataset} &gt; Share</Where>: give that
                    project's Vertex AI service agent <Ui>BigQuery Data Editor</Ui>. Put in the project number from
                    that project's Dashboard.
                    <Copy
                        label="Vertex AI service agent"
                        value="service-PROJECT_NUMBER@gcp-sa-aiplatform.iam.gserviceaccount.com"
                    />
                </Task>
            </Group>
        </Checklist>
    );
}

function CreateAccount({ org, project, dataset, lookup }) {
    return (
        <Checklist>
            <Group title={`With ${project} selected in the project picker`}>
                <Task>
                    <Where>IAM & Admin &gt; Service accounts</Where>: create a service account for Origin.
                </Task>
            </Group>
            <Group title={`With the org ${org} selected in the project picker`}>
                <Task>
                    <Where>IAM & Admin &gt; Roles &gt; Create role</Where>: name it anything, for example{" "}
                    <Mono>Origin Vertex connector</Mono>.
                </Task>
                <Task sub check={false}>
                    <Ui>Add permissions</Ui>: search for and add each of these.
                    {rolePermissions(lookup).map((permission) => (
                        <Copy key={permission} label={`Permission ${permission}`} value={permission} />
                    ))}
                </Task>
                <Task>
                    <Where>IAM & Admin &gt; IAM &gt; Grant access</Where>: give the service account the role you just
                    created.
                </Task>
            </Group>
            <Group title={`With ${project} selected in the project picker`}>
                <Task>
                    <Where>IAM & Admin &gt; IAM &gt; Grant access</Where>: give the service account{" "}
                    <Ui>BigQuery Job User</Ui>.
                </Task>
                <Task>
                    <Where>BigQuery &gt; {dataset} &gt; Share</Where>: give the service account{" "}
                    <Ui>BigQuery Data Viewer</Ui>.
                </Task>
                <Task>
                    <Where>IAM & Admin &gt; Service accounts &gt; the service account &gt; Keys &gt; Add key</Where>:{" "}
                    <Ui>Create new key</Ui>, JSON. It downloads once.
                </Task>
            </Group>
        </Checklist>
    );
}

function loggingCommand(project, dataset) {
    const config = {
        publisherModelConfig: {
            loggingConfig: {
                enabled: true,
                samplingRate: 1,
                bigqueryDestination: { outputUri: `bq://${project}.${dataset}.request_response_logging` },
            },
        },
        updateMask: "loggingConfig",
    };
    return [
        "curl -X POST",
        '-H "Authorization: Bearer $(gcloud auth print-access-token)"',
        '-H "Content-Type: application/json"',
        '"https://aiplatform.googleapis.com/v1beta1/projects/PROJECT_ID/locations/global/publishers/PUBLISHER/models/MODEL:setPublisherModelConfig"',
        `-d '${JSON.stringify(config)}'`,
    ].join(" ");
}

function TurnOnYourself({ project, dataset }) {
    return (
        <Checklist>
            <p className="m-0">
                Optional. Origin turns logging on for a model after it sees the first call, so that first call has no
                prompt. To capture models you already use from their first call, turn logging on for them yourself.
            </p>
            <Group title="For each model you want captured from its first call">
                <Task>
                    Open <Where>Cloud Shell</Where> and run this. Replace <Mono>PROJECT_ID</Mono> with the project that
                    calls the model, <Mono>PUBLISHER</Mono> with <Mono>google</Mono> or <Mono>anthropic</Mono>, and{" "}
                    <Mono>MODEL</Mono> with the model ID, for example <Mono>gemini-2.5-flash</Mono>.
                    <Copy label="Logging command" value={loggingCommand(project, dataset)} />
                </Task>
                <Task sub check={false}>
                    For a model in a region, change <Mono>aiplatform.googleapis.com</Mono> to{" "}
                    <Mono>REGION-aiplatform.googleapis.com</Mono> and <Mono>global</Mono> to the region.
                </Task>
                <Task sub check={false}>
                    Keep the sampling rate at 1 and the destination as shown. Calls logged anywhere else, or only some of
                    them, are not captured.
                </Task>
            </Group>
            <p className="m-0">
                Once Origin sees the model called, it manages this setting like the ones it turned on, and turns it off if
                you remove this org.
            </p>
        </Checklist>
    );
}

function GuideToggle({ open, onToggle }) {
    return (
        <button type="button" onClick={onToggle} aria-expanded={open} className={secondaryBtnCls}>
            {open ? "Hide steps" : "Show steps"}
        </button>
    );
}

function LookupChoice({ lookup, onChange, project }) {
    const option = (value, label) => (
        <button
            type="button"
            role="radio"
            aria-checked={lookup === value}
            onClick={() => onChange(value)}
            className={[
                "inline-flex items-center rounded-[2px] border px-[8px] py-[3px] text-[11px] leading-[1.2] transition-colors cursor-pointer",
                lookup === value
                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            {label}
        </button>
    );
    return (
        <div className="grid gap-[4px] text-[12px] leading-[1.45] text-[color:var(--ink)]">
            <div className="flex flex-wrap items-center gap-[8px]">
                <span>Attribute chat completions calls to</span>
                <span role="radiogroup" aria-label="Attribute chat completions calls to" className="inline-flex gap-[4px]">
                    {option(true, "Their project")}
                    {option(false, "The whole org")}
                </span>
            </div>
            <span className="text-[11px] text-[color:var(--muted)]">
                {lookup
                    ? `Adds one permission to the role. Also enable the Cloud Resource Manager API in ${project}.`
                    : "Those calls are attributed to the whole org, and a project-scoped connection drops them."}
            </span>
        </div>
    );
}

const LOGGING_TITLE = "Prompt logging turns on";
const LOGGING_SUMMARY = "Origin turns it on for each model your projects call.";
const READ_TITLE = "Confirm the first read";
const READ_SUMMARY = "Origin reads the dataset about once a minute.";

function KeyInput({ value, onChange }) {
    const [pasting, setPasting] = useState(false);
    const [fileName, setFileName] = useState("");
    const input = useRef(null);
    const readFile = useCallback(
        async (event) => {
            const file = event.target.files?.[0];
            if (file) {
                setFileName(file.name);
                onChange(await file.text());
            }
            event.target.value = "";
        },
        [onChange],
    );
    return (
        <div className="grid gap-[6px]">
            <span className="text-[12px] font-medium text-[color:var(--ink)]">Service account key</span>
            <div className="flex flex-wrap items-center gap-[10px]">
                <button type="button" onClick={() => input.current?.click()} className={secondaryBtnCls}>
                    Choose key file
                </button>
                <span className="text-[12px] text-[color:var(--muted)]">
                    {fileName || (value && !pasting ? "Key ready" : "No file chosen")}
                </span>
                {pasting ? null : (
                    <button type="button" onClick={() => setPasting(true)} className={linkCls}>
                        Paste it instead
                    </button>
                )}
                <input
                    ref={input}
                    type="file"
                    accept="application/json,.json"
                    aria-label="Key file"
                    onChange={readFile}
                    className="hidden"
                />
            </div>
            {pasting ? (
                <textarea
                    aria-label="Service account key"
                    value={value}
                    spellCheck={false}
                    autoComplete="off"
                    rows={4}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder='{"type": "service_account", ...}'
                    className={`${inputCls} py-[8px] font-mono`}
                />
            ) : null}
            <span className="text-[11px] text-[color:var(--muted)]">Stored encrypted and never shown again.</span>
        </div>
    );
}

function ProjectScope({ limited, onLimited, text, onText }) {
    return (
        <div className="grid gap-[6px]">
            <label className="flex cursor-pointer items-center gap-[8px] text-[12px] text-[color:var(--ink)]">
                <input type="checkbox" checked={limited} onChange={(event) => onLimited(event.target.checked)} />
                Only capture specific projects
            </label>
            {limited ? (
                <textarea
                    aria-label="Project IDs"
                    value={text}
                    spellCheck={false}
                    rows={2}
                    onChange={(event) => onText(event.target.value)}
                    placeholder="project-a, project-b"
                    className={`${inputCls} py-[8px] font-mono`}
                />
            ) : null}
        </div>
    );
}

function ConnectSteps({ onCreated }) {
    const caps = useMyPermissions();
    const [routeOpen, setRouteOpen] = useState(true);
    const [accountOpen, setAccountOpen] = useState(false);
    const [ownOpen, setOwnOpen] = useState(false);
    const [organizationId, setOrganizationId] = useState("");
    const [datasetProjectId, setDatasetProjectId] = useState("");
    const [datasetId, setDatasetId] = useState("");
    const [lookup, setLookup] = useState(true);
    const [limited, setLimited] = useState(false);
    const [projectsText, setProjectsText] = useState("");
    const [key, setKey] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const org = organizationId.trim();
    const project = datasetProjectId.trim();
    const dataset = datasetId.trim();
    const [confirmed, setConfirmed] = useState(false);
    const named = Boolean(org && project && dataset) && confirmed;
    const projectIds = limited ? projectList(projectsText) : [];
    const keyed = Boolean(key.trim());
    const canConnect = named && keyed && (!limited || projectIds.length > 0) && !saving;
    const consoleState = keyed ? "done" : named ? "current" : "todo";

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await createVertexIntegration({
                organizationId: org,
                datasetProjectId: project,
                datasetId: dataset,
                serviceAccountKey: key.trim(),
                projectIds,
            });
            setKey("");
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, dataset, key, onCreated, org, project, projectIds]);

    return (
        <Steps label="Connect a Google Cloud org">
            <Step
                number={1}
                state={named ? "done" : "current"}
                title="Name the org and the dataset"
                summary={
                    named ? (
                        <>
                            Org <Mono>{org}</Mono>, dataset <Mono>{`${project}.${dataset}`}</Mono>.
                        </>
                    ) : (
                        "Enter these yourself. Origin uses them to fill in the console instructions in the steps that follow."
                    )
                }
                action={
                    named ? (
                        <button type="button" onClick={() => setConfirmed(false)} className={secondaryBtnCls}>
                            Change
                        </button>
                    ) : null
                }
            >
                {named ? null : (
                    <div className="grid gap-[12px]">
                        <div className="grid items-start gap-[12px] sm:grid-cols-3">
                            <Field label="Organization ID" hint="IAM & Admin > Settings, with the org selected.">
                                <input
                                    type="text"
                                    value={organizationId}
                                    spellCheck={false}
                                    onChange={(event) => setOrganizationId(event.target.value)}
                                    placeholder="123456789012"
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>
                            <Field
                                label="Dataset project"
                                hint="The project that will hold the logs. Its ID is on the project's Dashboard."
                            >
                                <input
                                    type="text"
                                    value={datasetProjectId}
                                    spellCheck={false}
                                    onChange={(event) => setDatasetProjectId(event.target.value)}
                                    placeholder="my-logging-project"
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>
                            <Field label="Dataset name" hint="A new name. You create the dataset in step 2.">
                                <input
                                    type="text"
                                    value={datasetId}
                                    spellCheck={false}
                                    onChange={(event) => setDatasetId(event.target.value)}
                                    placeholder="origin_vertex"
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => setConfirmed(true)}
                                disabled={!(org && project && dataset)}
                                className={primaryBtnCls}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}
            </Step>
            <Step
                number={2}
                state={consoleState}
                title="Send Vertex AI audit logs to BigQuery"
                summary="Turn on audit logs and route them into the dataset, in the Google Cloud console."
                action={named ? <GuideToggle open={routeOpen} onToggle={() => setRouteOpen((open) => !open)} /> : null}
            >
                {named && routeOpen ? <RouteLogs org={org} project={project} dataset={dataset} /> : null}
            </Step>
            <Step
                number={3}
                state={consoleState}
                title="Create Origin's service account"
                summary="Origin signs in as this account to read the dataset and turn on prompt logging."
                action={
                    named ? <GuideToggle open={accountOpen} onToggle={() => setAccountOpen((open) => !open)} /> : null
                }
            >
                {named && accountOpen ? (
                    <div className="grid gap-[14px]">
                        <CreateAccount org={org} project={project} dataset={dataset} lookup={lookup} />
                        <LookupChoice lookup={lookup} onChange={setLookup} project={project} />
                    </div>
                ) : null}
            </Step>
            <Step
                number={4}
                state={keyed ? "current" : "todo"}
                title="Paste the key and connect"
                summary="Origin test-reads the dataset first and says what is missing."
            >
                {named ? (
                    <Panel className="grid gap-[14px] p-[14px]">
                        <KeyInput value={key} onChange={setKey} />
                        <ProjectScope limited={limited} onLimited={setLimited} text={projectsText} onText={setProjectsText} />
                        <div>
                            <button type="button" onClick={connect} disabled={!canConnect} className={primaryBtnCls}>
                                {saving ? "Connecting..." : "Connect org"}
                            </button>
                        </div>
                        <ErrorText>{error}</ErrorText>
                    </Panel>
                ) : null}
            </Step>
            <Step
                number={5}
                state="todo"
                title={LOGGING_TITLE}
                summary={LOGGING_SUMMARY}
                action={named ? <GuideToggle open={ownOpen} onToggle={() => setOwnOpen((open) => !open)} /> : null}
            >
                {named && ownOpen ? <TurnOnYourself project={project} dataset={dataset} /> : null}
            </Step>
            <Step number={6} state="todo" title={READ_TITLE} summary={READ_SUMMARY} last />
        </Steps>
    );
}

function UncapturedList({ models }) {
    return (
        <Panel className="grid gap-0">
            <ul className="m-0 grid max-h-[320px] list-none overflow-y-auto p-0">
                {models.map((model) => (
                    <li
                        key={`${model.projectId}/${model.location}/${model.model}`}
                        className="grid gap-[2px] border-t border-[color:var(--line)] px-[12px] py-[10px] text-[12px] leading-[1.4] first:border-t-0"
                    >
                        <span className="font-mono text-[color:var(--ink)]">
                            {model.projectId} / {model.location} / {model.model.replace(/^publishers\//, "")}
                        </span>
                        <span className="text-[color:var(--muted)]">{model.reason}</span>
                    </li>
                ))}
            </ul>
        </Panel>
    );
}

function ConnectionForm({ integration, onChanged, onClose }) {
    const caps = useMyPermissions();
    const [datasetProjectId, setDatasetProjectId] = useState(integration.datasetProjectId);
    const [datasetId, setDatasetId] = useState(integration.datasetId);
    const [limited, setLimited] = useState(integration.projectIds.length > 0);
    const [projectsText, setProjectsText] = useState(integration.projectIds.join(", "));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const projectIds = limited ? projectList(projectsText) : [];
    const moved =
        datasetProjectId.trim() !== integration.datasetProjectId || datasetId.trim() !== integration.datasetId;
    const rescoped =
        projectIds.length !== integration.projectIds.length ||
        projectIds.some((id) => !integration.projectIds.includes(id));
    const canSave =
        datasetProjectId.trim() && datasetId.trim() && (moved || rescoped) && (!limited || projectIds.length > 0);

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await changeVertexScope(integration.id, {
                datasetProjectId: datasetProjectId.trim(),
                datasetId: datasetId.trim(),
                projectIds,
            });
            onClose();
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, datasetId, datasetProjectId, integration.id, onChanged, onClose, projectIds]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <div className="grid items-start gap-[12px] sm:grid-cols-2">
                <Field label="Dataset project">
                    <input
                        type="text"
                        value={datasetProjectId}
                        spellCheck={false}
                        onChange={(event) => setDatasetProjectId(event.target.value)}
                        className={`${inputCls} font-mono`}
                    />
                </Field>
                <Field label="Dataset name">
                    <input
                        type="text"
                        value={datasetId}
                        spellCheck={false}
                        onChange={(event) => setDatasetId(event.target.value)}
                        className={`${inputCls} font-mono`}
                    />
                </Field>
            </div>
            {moved ? (
                <p className="m-0 text-[11px] text-[color:var(--muted)]">
                    Origin reads the new dataset from one day back and moves its prompt logging there.
                </p>
            ) : null}
            <ProjectScope limited={limited} onLimited={setLimited} text={projectsText} onText={setProjectsText} />
            <div className="flex flex-wrap items-center gap-[8px]">
                <button type="button" onClick={save} disabled={!canSave || saving} className={primaryBtnCls}>
                    {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={onClose} className={secondaryBtnCls}>
                    Cancel
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

function KeyForm({ integration, onChanged, onClose }) {
    const caps = useMyPermissions();
    const [key, setKey] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const replace = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await replaceVertexKey(integration.id, key.trim());
            onClose();
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, integration.id, key, onChanged, onClose]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <KeyInput value={key} onChange={setKey} />
            <div className="flex flex-wrap items-center gap-[8px]">
                <button type="button" onClick={replace} disabled={!key.trim() || saving} className={primaryBtnCls}>
                    {saving ? "Saving..." : "Save key"}
                </button>
                <button type="button" onClick={onClose} className={secondaryBtnCls}>
                    Cancel
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

function OrgSteps({ integration, directory, onChanged, refreshing }) {
    const [, loggingState, readState] = vertexSteps(integration);
    const noDirectory = readState === "done" && directoryMissing(directory);
    const scoped = readState === "done" && !noDirectory && directoryScoped(directory);
    const captureState = noDirectory || scoped ? "failed" : readState;
    const [editing, setEditing] = useState(null);
    const [guide, setGuide] = useState(null);
    const blocked = vertexBlocked(integration);
    const turningOn = vertexTurningOn(integration);
    const lastRead = whenLabel(integration.lastSuccessAt);
    const close = useCallback(() => setEditing(null), []);
    const toggle = (name) => () => setGuide((open) => (open === name ? null : name));
    const org = integration.organizationId;
    const project = integration.datasetProjectId;
    const dataset = integration.datasetId;
    const watching =
        integration.projectIds.length === 0
            ? "all projects"
            : integration.projectIds.length === 1
              ? integration.projectIds[0]
              : `${integration.projectIds.length} projects`;

    return (
        <Steps label={`Google Cloud org ${org}`}>
            <Step
                number={1}
                state="done"
                title="Name the org and the dataset"
                summary={
                    <>
                        Org <Mono>{org}</Mono>, dataset <Mono>{`${project}.${dataset}`}</Mono>.
                    </>
                }
            />
            <Step
                number={2}
                state="done"
                title="Send Vertex AI audit logs to BigQuery"
                summary="Done."
                action={<GuideToggle open={guide === "route"} onToggle={toggle("route")} />}
            >
                {guide === "route" ? <RouteLogs org={org} project={project} dataset={dataset} /> : null}
            </Step>
            <Step
                number={3}
                state="done"
                title="Create Origin's service account"
                summary="Done."
                action={<GuideToggle open={guide === "account"} onToggle={toggle("account")} />}
            >
                {guide === "account" ? <CreateAccount org={org} project={project} dataset={dataset} lookup /> : null}
            </Step>
            <Step
                number={4}
                state={editing ? "current" : "done"}
                title="Key saved"
                summary={
                    <>
                        Signed in as <Mono>{integration.serviceAccountEmail}</Mono>, capturing {watching}.
                    </>
                }
                action={
                    editing ? null : (
                        <>
                            <button type="button" onClick={() => setEditing("key")} className={secondaryBtnCls}>
                                Replace key
                            </button>
                            <button type="button" onClick={() => setEditing("scope")} className={secondaryBtnCls}>
                                Change dataset or projects
                            </button>
                        </>
                    )
                }
            >
                {editing === "key" ? <KeyForm integration={integration} onChanged={onChanged} onClose={close} /> : null}
                {editing === "scope" ? (
                    <ConnectionForm integration={integration} onChanged={onChanged} onClose={close} />
                ) : null}
            </Step>
            <Step
                number={5}
                state={loggingState}
                title={
                    loggingState === "failed"
                        ? `${blocked.length === 1 ? "1 model" : `${blocked.length} models`} not captured`
                        : LOGGING_TITLE
                }
                summary={
                    loggingState === "failed"
                        ? "Fix each one below. Origin retries daily."
                        : loggingState === "current"
                          ? `Turning on for ${turningOn.length === 1 ? "1 model" : `${turningOn.length} models`}.`
                          : integration.lastSuccessAt
                            ? "On for every model your projects call."
                            : LOGGING_SUMMARY
                }
                action={<GuideToggle open={guide === "own"} onToggle={toggle("own")} />}
            >
                {blocked.length > 0 ? <UncapturedList models={blocked} /> : null}
                {guide === "own" ? <TurnOnYourself project={project} dataset={dataset} /> : null}
            </Step>
            <Step
                number={6}
                state={captureState}
                title={
                    noDirectory
                        ? NO_DIRECTORY.label
                        : scoped
                          ? VERTEX_SCOPED_DIRECTORY.label
                          : captureState === "done"
                            ? "Capturing"
                            : READ_TITLE
                }
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : scoped
                          ? VERTEX_SCOPED_DIRECTORY.detail
                          : captureState === "failed"
                            ? integration.lastSyncError
                            : captureState === "done"
                              ? `Last read ${lastRead}.`
                              : READ_SUMMARY
                }
                action={
                    !noDirectory && !scoped && captureState !== "done" ? (
                        <button type="button" onClick={onChanged} disabled={refreshing} className={secondaryBtnCls}>
                            {refreshing ? "Checking..." : "Refresh"}
                        </button>
                    ) : null
                }
                last
            />
        </Steps>
    );
}

function Removing({ integration, onChanged, refreshing }) {
    return (
        <Panel className="grid justify-items-start gap-[10px] p-[14px]">
            <p className="m-0 text-[12.5px] leading-[1.5] text-[color:var(--ink)]">
                Removing org <Mono>{integration.organizationId}</Mono>. Origin is turning off the prompt logging it
                turned on; this tab goes away when it's done.
            </p>
            <button type="button" onClick={onChanged} disabled={refreshing} className={secondaryBtnCls}>
                {refreshing ? "Checking..." : "Refresh"}
            </button>
        </Panel>
    );
}

export default function VertexPage({
    source,
    health,
    entry,
    directory,
    onBack,
    onChanged,
    selectedItem,
    onSelectItem,
}) {
    const caps = useMyPermissions();
    const integrations = entry?.data ?? null;
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);
    const knownIds = useRef(null);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const created = useCallback(async () => {
        knownIds.current = new Set((integrations ?? []).map((integration) => integration.id));
        await onChanged();
    }, [integrations, onChanged]);

    useEffect(() => {
        if (!knownIds.current || !integrations) return;
        const added = integrations.find((integration) => !knownIds.current.has(integration.id));
        knownIds.current = null;
        if (added) onSelectItem(added.id);
    }, [integrations, onSelectItem]);

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteVertexIntegration(target.id);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged]);

    if (!integrations) {
        return (
            <SourcePage
                source={source}
                health={health}
                onBack={onBack}
                backLabel="Back to data sources"
                busy={!entry?.loaded || entry?.loading}
                aside={null}
            >
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="Google Cloud orgs"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: integration.organizationId,
                        mono: true,
                        dotTone: orgTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Connect org" },
                ]}
            />
        ) : null;

    const live = current && current.status !== "deleting";

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does in your Google Cloud">
                        <ul className="m-0 grid list-disc gap-[4px] pl-[16px]">
                            <li>Reads the logs in your dataset</li>
                            <li>Turns on prompt logging for models your projects call</li>
                        </ul>
                    </AsideSection>
                    <AsideSection title="Who is captured">
                        <p className="m-0">Each prompt is attributed to the Google Cloud project that made it. Google doesn't log the person.</p>
                    </AsideSection>
                    <AsideSection title="One connection per Google Cloud org">
                        <p className="m-0">It covers the whole org, or only the projects you list.</p>
                    </AsideSection>
                    {current ? null : (
                        <AsideSection title="Roles each step needs">
                            <p className="m-0">
                                One person with all of these can do the whole setup, or split the steps between people.
                            </p>
                            <ul className="m-0 grid list-disc gap-[4px] pl-[16px]">
                                <li>Step 2: Owner on the dataset project, and Logs Configuration Writer on the org.</li>
                                <li>
                                    Step 3: Owner on the dataset project, and Organization Role Administrator and
                                    Organization Administrator on the org. Add Organization Policy Administrator on the
                                    org if it blocks service account keys.
                                </li>
                                <li>Every other project that calls Vertex AI: Project IAM Admin on that project.</li>
                            </ul>
                        </AsideSection>
                    )}
                    {live ? (
                        <AsideSection title="Disconnect">
                            <p className="m-0">
                                Origin turns off the prompt logging it turned on. Prompts already captured are kept.
                            </p>
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(current)}
                                disabled={removing}
                                className={`${dangerBtnCls} w-fit`}
                            >
                                Disconnect
                            </button>
                            <ErrorText>{error}</ErrorText>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            {current?.status === "deleting" ? (
                <Removing integration={current} onChanged={onChanged} refreshing={entry.loading} />
            ) : current ? (
                <OrgSteps
                    key={current.id}
                    integration={current}
                    directory={directory}
                    onChanged={onChanged}
                    refreshing={entry.loading}
                />
            ) : (
                <ConnectSteps onCreated={created} />
            )}
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.VERTEX_DISCONNECT}
                eyebrow="Google Vertex AI"
                title="Disconnect this org?"
                body="Origin turns off the prompt logging it turned on, then removes the connection. Captured prompts are kept. Your audit logs, sink, dataset and service account stay."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SourcePage>
    );
}
